/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { useTenantScope } from "../../hooks/useTenantScope";
import { StatusBadge, emptyMessage } from "../ui";
import type { StatusTone } from "../ui/StatusBadge";
import {
  useActivateEventMutation,
  useDeleteEventMutation,
  useGetEventsQuery,
} from "../../store/services/events";
import { useDebounce } from "../../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import type { AppDispatch } from "../../store";
import type { SetStateAction } from "react";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  resolveFilterValue,
  resetEventsFilters,
  selectEventsFilters,
  setDateSortOrder,
  setEndDate,
  setEventId,
  setEventName,
  setEventStatus,
  setEventSubTypeId,
  setEventTypeId,
  setFeatured,
  setStartDate,
  type EventDateSortOrder,
} from "../../store/slices/eventsFiltersSlice";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { DataView } from "primereact/dataview";
import { EventFilters } from "../Filters/EventFilters";
import { Swiper, SwiperSlide } from 'swiper/react';
import { getEventImageSrc } from "../../utils/functions/getEventImageSrc";
import { Navigation, Pagination } from 'swiper/modules';
import { Badge } from "primereact/badge";
import {
  correctDateAndHours,
  extractHoursAndMinutes,
  formatDateWithoutSeconds,
} from "../../utils/functions/date";
import { convertToPrice } from "../../utils/functions/currencies";
import { SplitButton } from "primereact/splitbutton";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { SelectUsers } from "../SelectUsers";
import { DuplicateEventForm } from "../Forms/DuplicateEventForm";
import { EventMessageForm } from "../Forms/EventMessageForm";
import { Event } from "../../models/events";
import { getEventOperationalStatus } from "../../utils/functions/events";
import { SingleSelectUser } from "../SingleSelectUser";
import { CancelEventDialog } from "../Dialogs/CancelEventDialog";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { getApiErrorMessage, getApiErrorStatus } from "../../utils/functions/api-errors";

interface EventListDVProps {
  show?: "all" | "next";
}

const getEventsErrorMessage = (error: unknown): string => {
  const message = getApiErrorMessage(error);
  if (message) return message;

  const status = getApiErrorStatus(error);
  if (status) {
    return `No se pudieron cargar los eventos (${status}). Intentá de nuevo en unos minutos.`;
  }

  return "No se pudieron cargar los eventos. Intentá de nuevo en unos minutos.";
};

const createEventsFilterSetter =
  <T,>(
    dispatch: AppDispatch,
    action: (payload: T) => PayloadAction<T>,
    getCurrent: () => T
  ) =>
    (value: SetStateAction<T>) => {
      dispatch(action(resolveFilterValue(value, getCurrent())));
    };

/**
 * Chips de resumen de un evento.
 *
 * Antes este set estaba DUPLICADO: una copia con `hidden md:block` y otra con
 * `md:hidden`, idénticas salvo la clase — 13 <Badge> para 7 datos, y las dos
 * copias ya habían divergido (la de móvil no mostraba "Host asiste"). Se
 * renderiza una sola vez y el envoltorio hace flex-wrap.
 *
 * "Estado" usa StatusBadge porque sí es un estado de negocio (§3.2); el resto
 * son datos, no estados, así que van neutros.
 */
/** Dato del detalle: etiqueta chica arriba, valor debajo. */
const EventFact = ({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`flex flex-column ${className}`} style={{ gap: "var(--space-6)" }}>
    <span className="text-xs text-500 font-semibold uppercase">{label}</span>
    <span className="font-semibold text-900">{children}</span>
  </div>
);

const EventBadges = ({ event }: { event: Event }) => {
  const status = getEventOperationalStatus(event);
  const statusLabel =
    status === "finished" ? "Finalizado" : status === "active" ? "Activo" : "Inactivo";
  const statusTone: StatusTone =
    status === "finished" ? "info" : status === "active" ? "success" : "error";

  return (
    <span className="flex flex-wrap align-items-center gap-2">
      <StatusBadge label={statusLabel} tone={statusTone} />
      <Badge value={`Cupos: ${event?.amount}`} />
      <Badge value={`Disponibles: ${event?.available}`} severity="warning" />
      <Badge value={`Host asiste: ${event?.hostAssists ? "Sí" : "No"}`} severity="info" />
      <Badge value={`🤖 ${event?.fictional}`} />
      <Badge value={`🧑 ${event?.users}`} />
      {event.featured && (
        <Badge
          value={`⭐ Destacado${event.featuredOrder ? ` #${event.featuredOrder}` : ""}`}
        />
      )}
    </span>
  );
};

export const EventListDV = ({ show = "all" }: EventListDVProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    eventId,
    eventTypeId,
    eventSubTypeId,
    startDate,
    endDate,
    eventName,
    featured,
    eventStatus,
    dateSortOrder,
  } = useAppSelector(selectEventsFilters);

  // §6.4 / INC-02 — el tenant se hereda del selector global del header, no de un
  // filtro propio de la pantalla. Se sigue mandando como param porque la query
  // ya lo usaba; el valor ahora viene del scope.
  const tenantScope = useTenantScope();
  const tenantId = tenantScope.tenantId;

  const debouncedEventName = useDebounce(eventName, 500);
  const effectiveEventName = eventName === "" ? "" : debouncedEventName;
  const [eventToDelete, setEventToDelete] = useState<Event>();
  const [realUsersToAdd, setRealUsersToAdd] = useState<Event>();
  const [fictionalUsersToAdd, setFictionalUsersToAdd] = useState<Event>();
  const [hostToAdd, setHostToAdd] = useState<Event>();
  const [duplicateEvent, setDuplicateEvent] = useState<Event>();
  const [newEventMessage, setNewEventMessage] = useState<Event>();
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsRows, setEventsRows] = useState(5);
  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchEvents,
  } = useGetEventsQuery({
    id: eventId,
    startDate: eventStatus === "finished" ? undefined : startDate,
    endDate: eventStatus === "finished" ? undefined : endDate,
    name: effectiveEventName,
    typeId: eventTypeId,
    subTypeId: eventSubTypeId,
    tenantId: tenantId || undefined,
    page: eventsPage + 1,
    limit: eventsRows,
    show: eventStatus === "finished" ? undefined : show,
    featured,
    ...(eventStatus !== "all" ? { eventStatus } : {}),
    sortBy: "startDate",
    sortOrder: dateSortOrder,
  });

  React.useEffect(() => {
    setEventsPage(0);
  }, [
    eventId,
    eventTypeId,
    eventSubTypeId,
    startDate,
    endDate,
    effectiveEventName,
    tenantId,
    featured,
    eventStatus,
    dateSortOrder,
    show,
  ]);

  // Force refetch when tenantId changes to avoid caching issues
  React.useEffect(() => {
    if (tenantId !== undefined) {
      refetchEvents();
    }
  }, [tenantId, refetchEvents]);

  const [deleteEvent, { isLoading: isLoadingDelete }] =
    useDeleteEventMutation();


  const [activateEvent, { isLoading: isLoadingActivateEvent }] = useActivateEventMutation();
  const [showChangeEventStateDialog, setShowChangeEventStateDialog] = useState<boolean>();
  const [showCancelEventDialog, setShowCancelEventDialog] = useState(false);
  const [eventToUpdateState, setEventToUpdateState] = useState<Event>();
  const hideChangeEventStateDialog = () => setShowChangeEventStateDialog(false);
  const hideCancelEventDialog = () => {
    setShowCancelEventDialog(false);
    setEventToUpdateState(undefined);
  };

  const handleActivateEvent = async (id?: number) => {
    if (!id) return;

    try {
      await activateEvent(id).unwrap();
      hideChangeEventStateDialog();
    } catch (err: any) {
      console.error("Failed to change event state:", err);
      const reason =
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.error ||
        "el servidor rechazó el cambio de estado";
      toast.error(`No se pudo cambiar el estado del evento: ${reason}`);
    }
  };

  const hideDeleteEventDialog = () => setEventToDelete(undefined);
  const hideAddRealUsersDialog = () => {
    setRealUsersToAdd(undefined);
  };

  const hideAddFictionalUsersDialog = () => {
    setFictionalUsersToAdd(undefined);
  };

  const hideAddHostDialog = () => setHostToAdd(undefined);
  const hideNewEventMessageDialog = () => setNewEventMessage(undefined);
  const hideDuplicateEventDialog = () => setDuplicateEvent(undefined);

  const handleClearAllFilters = () => {
    dispatch(resetEventsFilters());
  };

  const toggleDateSortOrder = () => {
    const next: EventDateSortOrder = dateSortOrder === "asc" ? "desc" : "asc";
    dispatch(setDateSortOrder(next));
  };

  const handleEventsPageChange = (event: { first: number; rows: number }) => {
    setEventsPage(Math.floor(event.first / event.rows));
    setEventsRows(event.rows);
  };

  const handleDeleteEvent = (id: number) => {
    deleteEvent(id)
      .unwrap()
      .then(() => {
        toast.success("Evento eliminado exitosamente!");
      })
      .catch(() => toast.error("No se pudo eliminar el evento"))
      .finally(() => hideDeleteEventDialog());
  };

  return (
    <>
      {isError && (
        <div className="p-error mb-3 font-semibold">
          {getEventsErrorMessage(error)}
        </div>
      )}
      <DataView
        loading={isLoading || isLoadingDelete || isLoadingActivateEvent}
        value={data?.events ?? []}
        lazy
        paginator
        first={eventsPage * eventsRows}
        rows={eventsRows}
        totalRecords={data?.pagination?.total ?? data?.events.length ?? 0}
        rowsPerPageOptions={[5, 10, 25, 50]}
        onPage={handleEventsPageChange}
        emptyMessage={emptyMessage("eventos")}
        header={
          <EventFilters
            eventId={eventId}
            setEventId={createEventsFilterSetter(dispatch, setEventId, () => eventId)}
            startDate={startDate}
            setStartDate={createEventsFilterSetter(dispatch, setStartDate, () => startDate)}
            endDate={endDate}
            setEndDate={createEventsFilterSetter(dispatch, setEndDate, () => endDate)}
            eventName={eventName}
            setEventName={createEventsFilterSetter(dispatch, setEventName, () => eventName)}
            eventTypeId={eventTypeId}
            setEventTypeId={createEventsFilterSetter(dispatch, setEventTypeId, () => eventTypeId)}
            eventSubTypeId={eventSubTypeId}
            setEventSubTypeId={createEventsFilterSetter(
              dispatch,
              setEventSubTypeId,
              () => eventSubTypeId
            )}
            featured={featured}
            setFeatured={createEventsFilterSetter(dispatch, setFeatured, () => featured)}
            eventStatus={eventStatus}
            setEventStatus={createEventsFilterSetter(
              dispatch,
              setEventStatus,
              () => eventStatus
            )}
            dateSortOrder={dateSortOrder}
            onToggleSortOrder={toggleDateSortOrder}
            onClearAll={handleClearAllFilters}
          />
        }

        listTemplate={(items: Event[]): React.ReactNode[] => {
          let lastRenderedDate: string | null = null;

          return items.map((event, index) => {
            const validateDate = correctDateAndHours(event) < new Date();



            const eventDate = format(new Date(event.startDate).setHours(0, 0, 0, 0), "EEEE - dd/MM/yyyy", { locale: es });

            // Capitalize the first letter
            const formattedDate = eventDate.charAt(0).toUpperCase() + eventDate.slice(1);

            const showHeader = formattedDate !== lastRenderedDate;
            lastRenderedDate = formattedDate;

            return (
              <React.Fragment key={event.id}>
                {showHeader && (
                  <h2 className="event-day-header text-xxl font-bold text-primary">{formattedDate}</h2>
                )}

                <div
                  key={event.id}
                  /* mb-3 hace falta: el DataView envuelve los items en `grid-nogutter`,
                     que anula el padding de los col-*, así que sin margen las
                     tarjetas se tocan. Es el caso opuesto al de FormSection. */
                  className="event-card col-12 flex flex-column p-3 gap-3 mb-3"
                >
                  {/* Fila superior: miniatura, cabecera y acciones. La grilla de
                      datos va aparte y a ancho completo. Si acompañara a la
                      imagen en la misma fila quedaba un vacío debajo de la
                      miniatura (150px de alto contra ~700px de contenido), y
                      encima le comía ancho: los datos entraban en 2 columnas
                      con imagen y en 4 sin ella. */}
                  <div className="flex flex-column sm:flex-row align-items-start gap-4">
                  {event.imageUrls && event.imageUrls.length > 0 ? (
                    <div className="flex-shrink-0 border-round overflow-hidden" style={{ width: '260px', height: '150px', position: 'relative' }}>
                      <Swiper
                        modules={[Navigation, Pagination]}
                        spaceBetween={0}
                        slidesPerView={1}
                        navigation={true}
                        pagination={{ clickable: true }}
                        style={{ width: '100%', height: '100%' }}
                        allowTouchMove={true}
                        grabCursor={true}
                        centeredSlides={true}
                        loop={false}
                        watchSlidesProgress={true}
                        className="image-carousel"
                      >
                        {/* Include the original event image as first slide if it exists */}
                        {(event.imageBase64 || event.imageFormat) && (
                          <SwiperSlide key={`${event.id}-slide-original`}>
                            <div style={{
                              width: '260px',
                              height: '150px',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}>
                              <img
                                src={getEventImageSrc(event.imageFormat, event.imageBase64)}
                                alt={`${event.name} - Principal`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}
                              />
                            </div>
                          </SwiperSlide>
                        )}
                        {/* Include all additional images from imageUrls */}
                        {event.imageUrls.map((imageUrl, index) => (
                          <SwiperSlide key={`${event.id}-slide-${index}`}>
                            <div style={{
                              width: '260px',
                              height: '150px',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}>
                              <img
                                src={imageUrl}
                                alt={`${event.name} - ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}
                              />
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  ) : (
                    /* Misma caja que la rama con galería: antes esta era un
                       <Image height="170" width="auto">, así que las tarjetas
                       con y sin imágenes adicionales tenían miniaturas de
                       tamaños distintos y la fila no alineaba entre eventos. */
                    <div
                      className="flex-shrink-0 border-round overflow-hidden surface-100"
                      style={{ width: "260px", height: "150px" }}
                    >
                      <img
                        src={getEventImageSrc(event.imageFormat, event.imageBase64)}
                        alt={event.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </div>
                  )}
                  <div
                    /* min-w-0: sin esto un hijo flex no puede encogerse por
                       debajo del ancho de su contenido, así que empujaba el
                       bloque de acciones fuera de la card. */
                    className="cursor-pointer flex flex-column flex-1 min-w-0 gap-2"
                    onClick={() =>
                      navigate(event.id ? `/events/${event.id.toString()}` : "")
                    }
                  >
                    <div className="flex flex-column align-items-stretch w-full gap-3">
                      <div className="flex flex-column gap-2">
                        <h2 className="text-2xl font-bold text-900 p-0 m-0 flex flex-wrap align-items-baseline gap-2">
                          {event.name}
                          <span className="text-sm text-400 font-semibold">(ID: #{event.id})</span>
                        </h2>
                        <EventBadges event={event} />
                        {event.description && (
                          <p className="text-600 p-0 m-0 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {event.description}
                          </p>
                        )}
                      </div>

                    </div>
                  </div>
                  {/* flex-shrink-0 para que el bloque de acciones no se comprima
                      y ml-auto para que quede pegado a la derecha sin empujar el
                      contenido fuera de la card, que era el desborde. */}
                  <div className="flex align-items-start flex-shrink-0 ml-auto">
                    <SplitButton
                      label="Acciones"
                      model={
                        !validateDate
                          ? [
                            {
                              label: "Editar",
                              icon: "pi pi-pencil",
                              command: () =>
                                navigate(`/events/${event.id}/edit`),
                            },
                            {
                              label: "Enviar mensaje",
                              icon: "pi pi-send",
                              command: () => setNewEventMessage(event),
                            },
                            {
                              label: "Seleccionar host",
                              icon: "pi pi-user-plus",
                              command: () => setHostToAdd(event),
                            },
                            {
                              label: "Agregar usuarios reales",
                              icon: "pi pi-users",
                              command: () => setRealUsersToAdd(event),
                            },
                            {
                              label: "Agregar usuarios ficticios",
                              icon: "pi pi-users",
                              command: () => setFictionalUsersToAdd(event),
                            },
                            {
                              label: "Duplicar evento",
                              icon: "pi pi-clone",
                              command: () => setDuplicateEvent(event),
                            },
                            ...(event.active ? [{
                              label: "Desactivar",
                              icon: "pi pi-eye-slash",
                              command: () => {
                                setEventToUpdateState(event);
                                setShowCancelEventDialog(true);
                              }
                            }] : !event.deactivatedAt ? [{
                              // Created inactive and never deactivated: still activatable
                              label: "Activar",
                              icon: "pi pi-eye",
                              command: () => {
                                setShowChangeEventStateDialog(true);
                                setEventToUpdateState(event)
                              }
                            }] : []),
                            {
                              label: "Eliminar",
                              icon: "pi pi-times",
                              command: () => setEventToDelete(event),
                            },
                          ]
                          : [
                            {
                              label: "Duplicar evento",
                              icon: "pi pi-clone",
                              command: () => setDuplicateEvent(event),
                            },
                            {
                              label: "Eliminar",
                              icon: "pi pi-times",
                              command: () => setEventToDelete(event),
                            },
                          ]
                      }
                      outlined
                      severity="secondary"
                      size="small"
                    />
                  </div>
                  </div>

                  <hr className="w-full m-0 border-none border-top-1 surface-border" />

                  {/* Grilla que se adapta al ancho real disponible, no al
                      viewport: ver .event-facts en layout.css. */}
                  <div className="event-facts">
                    <EventFact label="Fecha">
                      {formatDateWithoutSeconds(event.startDate)}
                    </EventFact>
                    <EventFact label="Duración">
                      {extractHoursAndMinutes(event?.duration)}hs
                    </EventFact>
                    <EventFact label="Espacio">{event.space || "—"}</EventFact>
                    <EventFact label="Costo">{convertToPrice(event?.price || 0)}</EventFact>
                    <EventFact label="Inscripción">
                      {convertToPrice(event.inscription)}
                    </EventFact>
                    <EventFact label="Tipo">
                      <span className="flex flex-wrap align-items-center gap-2 text-sm">
                        <span className="flex align-items-center gap-1">
                          <i className="pi pi-tag text-xs" />
                          {event.eventTypeName}
                        </span>
                        <span className="flex align-items-center gap-1">
                          <i className="pi pi-tag text-xs" />
                          {event.eventSubTypeName}
                        </span>
                      </span>
                    </EventFact>
                    <EventFact label="Ubicación" className="event-facts__wide">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${event.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary flex align-items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="pi pi-map-marker text-sm" />
                        {event?.address}
                      </a>
                    </EventFact>
                  </div>
                </div>
              </React.Fragment>
            );
          });
        }}
      />
      {eventToDelete && (
        <Dialog
          header="Eliminar evento"
          visible={!!eventToDelete}
          style={{ width: "50vw" }}
          onHide={hideDeleteEventDialog}
          footer={
            <div>
              <Button
                severity="danger"
                label="Eliminar"
                onClick={() => handleDeleteEvent(eventToDelete.id)}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={hideDeleteEventDialog}
              />
            </div>
          }
        >
          <h1 className="text-3xl text-red-500 m-0 p-0">
            <i className="pi pi-exclamation-triangle"></i> ¡Cuidado!
          </h1>
          <p className="m-0 p-0">
            ¿Estas seguro que desea eliminar el evento{" "}
            <b>{eventToDelete?.name}</b>?
          </p>
        </Dialog>
      )}
      {realUsersToAdd && (
        <Dialog
          header="Agregar usuarios reales"
          visible={!!realUsersToAdd}
          style={{ width: "90vw" }}
          onHide={hideAddRealUsersDialog}
        >
          <SelectUsers
            mode="user"
            eventId={realUsersToAdd.id}
            handleClose={hideAddRealUsersDialog}
          />
        </Dialog>
      )}
      {fictionalUsersToAdd && (
        <Dialog
          header="Agregar usuarios ficticios"
          visible={!!fictionalUsersToAdd}
          style={{ width: "90vw" }}
          onHide={hideAddFictionalUsersDialog}
        >
          <SelectUsers
            mode="fictional"
            eventId={fictionalUsersToAdd.id}
            handleClose={hideAddFictionalUsersDialog}
          />
        </Dialog>
      )}
      {duplicateEvent && (
        <Dialog
          header="Duplicar evento"
          visible={!!duplicateEvent}
          style={{ width: "40vw" }}
          onHide={hideDuplicateEventDialog}
        >
          <DuplicateEventForm
            event={duplicateEvent}
            handleClose={hideDuplicateEventDialog}
          />
        </Dialog>
      )}
      {hostToAdd && (
        <Dialog
          header="Seleccionar host"
          visible={!!hostToAdd}
          style={{ width: "90vw" }}
          onHide={hideAddHostDialog}
        >
          <SingleSelectUser
            eventId={hostToAdd.id}
            hostAssists={hostToAdd.hostAssists}
            handleClose={hideAddHostDialog}
          />
        </Dialog>
      )}
      {newEventMessage && (
        <Dialog
          header="Nuevo mensaje para el evento"
          visible={!!newEventMessage}
          style={{ width: "40vw" }}
          onHide={hideNewEventMessageDialog}
        >
          <EventMessageForm
            eventId={newEventMessage.id}
            userId={1}
            handleClose={hideNewEventMessageDialog}
          />
        </Dialog>
      )}
      {showChangeEventStateDialog && (
        <Dialog
          header="Activar evento"
          visible={!!showChangeEventStateDialog}
          style={{ width: "40vw" }}
          onHide={hideChangeEventStateDialog}
          footer={
            <div>
              <Button
                severity="success"
                label="Activar"
                onClick={() => handleActivateEvent(eventToUpdateState?.id)}
              />
              <Button
                severity="secondary"
                label="Cancelar"
                onClick={hideChangeEventStateDialog}
              />
            </div>
          }
        >
          <p>Está por activar este evento. Pasará a estar visible en la App.</p>
        </Dialog>
      )}
      {showCancelEventDialog && (
        <CancelEventDialog
          visible={showCancelEventDialog}
          event={eventToUpdateState}
          onHide={hideCancelEventDialog}
        />
      )}
    </>

  );
};
